import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Container,
  Alert,
  Badge,
  Table,
  Button,
  Collapse,
  Card,
  Row,
  Col,
  Form
} from 'react-bootstrap';
import { useDashboardData } from '../hooks/useDashboardData';
import '../styles/OrderList.css'

export default function DealerDashboardPage() {
  const {
    user,
    allOrders: orders = [],
    error,
    submissionMessage,
    getTasksForStage,
    stageMap,
    getStageBadgeVariant,
    formatDate,
    allOrderStages = [],
    availableOrderTypes = [],
    groupTasksForDisplay
  } = useDashboardData();

  // Locally define search and filters (useDashboardData no longer handles them)
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterOrderType, setFilterOrderType] = useState('All');
  const topScrollRef = useRef(null);
  const topInnerRef = useRef(null);
  const tableScrollRef = useRef(null);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // Search filter
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        order.orderId?.toString().toLowerCase().includes(q) ||
        order.invoiceNumber?.toString().toLowerCase().includes(q) ||
        order.customerName?.toLowerCase().includes(q) ||
        order.dealerName?.toLowerCase().includes(q) ||
        order.orderType?.toLowerCase().includes(q) ||
        order.shippingMethod?.toLowerCase().includes(q);

      // Status filter
      const matchesStatus = filterStatus === 'All' || order.currentStage === filterStatus;

      // Order type filter
      const matchesOrderType = filterOrderType === 'All' || order.orderType === filterOrderType;

      return matchesSearch && matchesStatus && matchesOrderType;
    });
  }, [orders, searchTerm, filterStatus, filterOrderType]);

  useEffect(() => {
    if (!topScrollRef.current || !topInnerRef.current || !tableScrollRef.current) return;

    const topScroll = topScrollRef.current;
    const topInner = topInnerRef.current;
    const tableScroll = tableScrollRef.current;

    topInner.style.width = `${tableScroll.scrollWidth}px`;

    const handleTopScroll = () => { tableScroll.scrollLeft = topScroll.scrollLeft; };
    const handleTableScroll = () => { topScroll.scrollLeft = tableScroll.scrollLeft; };

    topScroll.addEventListener('scroll', handleTopScroll);
    tableScroll.addEventListener('scroll', handleTableScroll);

    const resizeObserver = new ResizeObserver(() => {
      topInner.style.width = `${tableScroll.scrollWidth}px`;
    });
    resizeObserver.observe(tableScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableScroll.removeEventListener('scroll', handleTableScroll);
      resizeObserver.disconnect();
    };
  }, [filteredOrders]);

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (orderId) => {
    setExpandedRows(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('All');
    setFilterOrderType('All');
  };

  // Early return
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Dealer') { return <Navigate to="/dashboard" replace />; }

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);

    const day = d.getDay();

    // If Saturday, move to Monday
    if (day === 6) {
      d.setDate(d.getDate() + 2);
    }

    // If Sunday, move to Monday
    if (day === 0) {
      d.setDate(d.getDate() + 1);
    }

    return d;
  };


  return (
    <Container
      fluid
      style={{
        backgroundColor: '#4595ad29',
        minHeight: '100vh',
        paddingTop: '2rem',
        paddingBottom: '2rem',
        margin: 0
      }}
    >
      <Container className="my-5">
        {/* Welcome card */}
        <Row className="justify-content-center">
          <Col md={10}>
            <Card className="shadow-lg p-4 mb-4">
              <Card.Body>
                <h1 className="mb-2 display-6 text-center">Welcome, {user?.username}!</h1>
                <p className="lead text-center mb-0">
                  Here you can track the progress of your dealership's orders.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {submissionMessage.message && (
          <Alert variant={submissionMessage.type}>{submissionMessage.message}</Alert>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Info Box for Lead Times */}
        <Row className="mb-4 justify-content-center">
          <Col md={10}>
            <Alert variant="info" className="shadow-sm">
              <h5 className="mb-2 text-center">Lead Time & Timeline Information</h5>
              <div className="text-start">
                <p>
                  Production on an order begins once payment is received 
                  (with the exception of warranty orders, certain parts orders, and other special cases). 
                  Once payment is finalized, your order officially enters the production process, and our lead time begins.
                  Our current lead time is approximately <strong>28–30 days</strong>, though this may vary depending on the season.
                </p>

                <h6>Reference Timeline:</h6>
                <ul>
                  <li>
                    <strong>Administration:</strong> Includes order creation, invoice creation, and payment processing.
                    This stage’s duration depends on how quickly payment is completed.
                  </li>
                  <li>
                    <strong>Pre-Production:</strong> Once payment is received, the order moves into the Pre-Production backlog. 
                    Expected time at this stage is 1–2 weeks, depending on order complexity.
                  </li>
                  <li>
                    <strong>Manufacturing:</strong> After Pre-Production, the order enters the manufacturing backlog. 
                    At this point, a production date will be provided once scheduled. 
                    This stage includes building and completing the order. Expected time at this stage is 1-2 weeks, depending on complexity.
                  </li>
                  <li>
                    <strong>Shipping:</strong> Once the order is complete, shipping usually takes 0–2 days, depending on piggyback or combined shipments.
                  </li>
                </ul>

                <p className="mb-0">
                  <strong>Note: </strong>If an order is taking longer than expected at any stage, please contact our office. 
                  We’ll reach out to the appropriate managers and provide an update as soon as possible.
                </p>
              </div>
            </Alert>
          </Col>
        </Row>

        {/* Search & Filters */}
        <Row className="mb-4 justify-content-center">
          <Col md={10}>
            <Card className="shadow-sm p-3">
              <Row className="align-items-center">
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Group controlId="dealerSearchOrders">
                    <Form.Label>Search Orders</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search by Order ID, Invoice #, Customer, Shipping Method, etc."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Group controlId="dealerFilterStatus">
                    <Form.Label>Production Stage</Form.Label>
                    <Form.Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      {allOrderStages.map(stage => (
                        <option key={stage} value={stage}>{stage}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="dealerFilterOrderType">
                    <Form.Label>Order Type</Form.Label>
                    <Form.Select
                      value={filterOrderType}
                      onChange={(e) => setFilterOrderType(e.target.value)}
                    >
                      {availableOrderTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col className="text-end">
                  <Button variant="outline-secondary" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <h3 className="mb-3">Your Dealership Orders</h3>

        {filteredOrders.length === 0 ? (
          <Alert>No orders found.</Alert>
        ) : (

          <div className="table-scroll-top-wrapper">
            {/* Top scrollbar */}
            <div className="top-scroll" ref={topScrollRef}>
              <div className="top-scroll-inner" ref={topInnerRef} />
            </div>

            {/* Actual table */}
            <div className="table-responsive" ref={tableScrollRef}>
              <Table bordered hover className="shadow-sm rounded order-list-table" style={{ minWidth: '1200px' }}>
                <thead className="table-dark">
                  <tr>
                    <th>Order ID</th>
                    <th>Invoice #</th>
                    <th>Order Type</th>
                    <th>Stage</th>
                    <th>Customer</th>
                    <th>Custom Work</th>
                    <th>Shipping Method</th>
                    <th>Order Date</th>
                    <th>Payment Date</th>
                    <th>PreProduction Received</th>
                    <th>Manufacturing Received</th>
                    <th>Build Started</th>
                    <th>Completion Date</th>
                    <th>Ship/Pickup Date</th>
                    <th>Progress</th>
                    <th>Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => {
                    const tasks = getTasksForStage(order.currentStage);
                    const stageKey = stageMap[order.currentStage];
                    const stageData = order[stageKey] || {};

                    const completedTasks = tasks.filter(t =>
                      t.type === 'checkbox' ? stageData[t.id] : !!stageData[t.id]
                    );

                    const isCompleted = order.currentStage === 'Completed';

                    // Dates for the columns
                    const orderDate = formatDate(order.orderDate, false);
                    const paidDate = order.administration?.paymentCompletedDate
                      ? formatDate(order.administration.paymentCompletedDate, false)
                      : 'N/A';
                    const preProductionReceived = order.administration?.completionDate
                      ? formatDate(order.administration.completionDate, false)
                      : 'N/A';
                      const manufacturingReceived = order.preProduction.completionDate
                      ? formatDate(order.preProduction.completionDate, false)
                      : 'N/A';
                    const productionDate = order.manufacturing?.orderEnteredProductionDate
                      ? formatDate(order.manufacturing.orderEnteredProductionDate, false)
                      : 'N/A';
                    const completionDate = order.completionDate
                      ? formatDate(order.completionDate, false)
                      : 'N/A';

                    let shipDate = 'N/A';

                    // Actual shipped date is always priority
                    if (order.shipping?.shippedDate) {
                      shipDate = formatDate(order.shipping.shippedDate, false);
                    }

                    // Estimated date (shipping orders only)
                    else if (order.shippingMethod !== 'Pickup') {
                      let estimateBaseDate = null;
                      let estimateDays = 0;

                      if (order.completionDate) {
                        estimateBaseDate = order.completionDate;
                        estimateDays = 2;
                      } else if (order.manufacturing?.orderEnteredProductionDate) {
                        estimateBaseDate = order.manufacturing.orderEnteredProductionDate;
                        estimateDays = 10;
                      } else if (order.preProduction?.completionDate) {
                        estimateBaseDate = order.preProduction.completionDate;
                        estimateDays = 15;
                      } else if (order.administration?.completionDate) {
                        estimateBaseDate = order.administration.completionDate;
                        estimateDays = 20;
                      } else if (order.administration?.paymentCompletedDate) {
                        estimateBaseDate = order.administration.paymentCompletedDate;
                        estimateDays = 30;
                      }

                      if (estimateBaseDate) {
                        const estimatedShipDate = addDays(
                          estimateBaseDate,
                          estimateDays
                        );

                        shipDate = (
                          <span className="text-muted fst-italic">
                            {formatDate(estimatedShipDate, false)}
                            <span className="fw-bold ms-1">(Est.)</span>
                          </span>
                        );
                      }
                    }

                    return (
                      <React.Fragment key={order.orderId}>
                        <tr>
                          <td>{order.orderId}</td>
                          <td>{order.invoiceNumber || 'N/A'}</td>
                          <td>{order.orderType || 'N/A'}</td>
                          <td><Badge bg={getStageBadgeVariant(order.currentStage)}>{order.currentStage}</Badge></td>
                          <td>{order.customerName}</td>
                          <td>{order.customWork || 'None'}</td>
                          <td>{order.shippingMethod || 'N/A'}</td>
                          <td>{orderDate}</td>
                          <td>{paidDate}</td>
                          <td>{preProductionReceived}</td>
                          <td>{manufacturingReceived}</td>
                          <td>{productionDate}</td>
                          <td>{completionDate}</td>
                          <td>{shipDate}</td>

                          {/* Progress column */}
                          <td>
                            {isCompleted
                              ? <>Completed</>
                              : <>{completedTasks.length} / {tasks.length} tasks completed</>
                            }
                          </td>

                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={isCompleted}                  // disable when order is completed
                              onClick={() => !isCompleted && toggleRow(order.orderId)}   // prevent toggle
                              aria-expanded={!!expandedRows[order.orderId]}
                            >
                              {isCompleted
                                ? 'Order Completed'
                                : expandedRows[order.orderId] ? 'Hide Details' : 'View Details'}
                            </Button>
                          </td>
                        </tr>

                        {/* Expanded details row */}
                        <tr>
                          <td colSpan="16" className="p-0">
                            <Collapse in={!!expandedRows[order.orderId]}>
                              <div className="p-3 bg-light border-top">

                                {/* Manufacturing gets the 3-column grouped layout */}
                                {order.currentStage === 'Manufacturing' ? (
                                  (() => {
                                    const grouped = groupTasksForDisplay(tasks);

                                    // Extract groups
                                    const manager = grouped.find(g => g.group === 'Manager Tasks');
                                    const welder = grouped.find(g => g.group === 'Welder Tasks');
                                    const assembler = grouped.find(g => g.group === 'Assembler Tasks');
                                    const seamstress = grouped.find(g => g.group === 'Seamstress Tasks');

                                    // Seamstress subgroups
                                    const drawingTasks = seamstress?.subtasks?.find(s => s.subgroup === 'Drawing') || { tasks: [] };
                                    const cuttingTasks = seamstress?.subtasks?.find(s => s.subgroup === 'Cutting') || { tasks: [] };
                                    const sewingTasks = seamstress?.subtasks?.find(s => s.subgroup === 'Sewing') || { tasks: [] };
                                    const completionTasks = seamstress?.subtasks?.find(s => s.subgroup === 'Packing & Completing') || { tasks: [] };

                                    // Build columns
                                    const col1 = [manager, welder, assembler].filter(Boolean);
                                    const col2 = [
                                      { group: 'Seamstress Tasks', subtasks: [drawingTasks, cuttingTasks] }
                                    ];
                                    const col3 = [
                                      { group: 'Seamstress Tasks', subtasks: [sewingTasks, completionTasks] }
                                    ];

                                    const isTaskCompleted = (task) => {
                                      return task.type === 'checkbox'
                                        ? stageData[task.id]
                                        : !!stageData[task.id];
                                    };

                                    const renderTask = (task) => {
                                      const completed = isTaskCompleted(task);
                                      const value =
                                        task.type === 'date' && stageData[task.id]
                                          ? formatDate(stageData[task.id], false)
                                          : stageData[task.id];

                                      return (
                                        <div
                                          key={task.id}
                                          className="mb-1"
                                          style={{ color: completed ? 'green' : 'inherit' }}
                                        >
                                          <span>{task.label}</span>
                                          {task.type === 'date' && value && (
                                            <> — <span>{value}</span></>
                                          )}
                                        </div>
                                      );
                                    };

                                    const renderGroup = (g) => (
                                      <div key={g.group} className="mb-3">
                                        <h6 className="fw-bold text-center">{g.group}</h6>

                                        {/* Non-seamstress groups */}
                                        {g.tasks &&
                                          g.tasks.map(task => renderTask(task))
                                        }

                                        {/* Seamstress split groups */}
                                        {g.subtasks &&
                                          g.subtasks.map(sub => (
                                            <div key={sub.subgroup} className="mb-2">
                                              <strong className="d-block mt-2 mb-1">{sub.subgroup}:</strong>
                                              {sub.tasks.map(task => renderTask(task))}
                                            </div>
                                          ))
                                        }
                                      </div>
                                    );

                                    return (
                                      <Row className="gx-4">
                                        <Col md={4}>{col1.map(renderGroup)}</Col>
                                        <Col md={4}>{col2.map(renderGroup)}</Col>
                                        <Col md={4}>{col3.map(renderGroup)}</Col>
                                      </Row>
                                    );
                                  })()
                                ) : order.currentStage === 'Administration' ? (
                                  <Row className="gx-4">
                                    {/* Column 1: Info Box */}
                                    <Col md={6}>
                                      <Card className="shadow-sm">
                                        <Card.Body>
                                          <h6 className="mb-2 fw-bold text-center">
                                            ** Note for Reskin / Remod / Repair Orders **
                                          </h6>

                                          <div className="small text-start">
                                            <p className="mb-2">
                                              The following additional steps must be completed <strong>prior to creating the invoice</strong>:
                                            </p>

                                            <ul className="mb-0 ps-3 text-start">
                                              <li>Determine if requested changes are possible</li>
                                              <li>Deliver copy of specifications to Sewing Supervisor</li>
                                              <li>Perform frame & components calculations</li>
                                              <li>Deliver calculations for invoice creation</li>
                                            </ul>
                                          </div>
                                        </Card.Body>
                                      </Card>
                                    </Col>

                                    {/* Column 2: Administration Tasks */}
                                    <Col md={6}>
                                      {tasks.map(task => {
                                        const completed = task.type === 'checkbox'
                                          ? stageData[task.id]
                                          : !!stageData[task.id];

                                        const value =
                                          task.type === 'date' && stageData[task.id]
                                            ? formatDate(stageData[task.id], false)
                                            : stageData[task.id];

                                        return (
                                          <div
                                            key={task.id}
                                            className="mb-2"
                                            style={{ color: completed ? 'green' : 'inherit' }}
                                          >
                                            <span>{task.label}</span>
                                            {task.type === 'date' && value && (
                                              <> — <span>{value}</span></>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </Col>

                                    
                                  </Row>
                                ) : (

                                  // Fallback for non-manufacturing stages
                                  <Row>
                                    <Col md={12}>
                                      {tasks.map(task => {
                                        const completed = task.type === 'checkbox'
                                          ? stageData[task.id]
                                          : !!stageData[task.id];

                                        const value =
                                          task.type === 'date' && stageData[task.id]
                                            ? formatDate(stageData[task.id], false)
                                            : stageData[task.id];

                                        return (
                                          <div
                                            key={task.id}
                                            className="mb-2"
                                            style={{ color: completed ? 'green' : 'inherit' }}
                                          >
                                            <span>{task.label}</span>
                                            {task.type === 'date' && value && (
                                              <> — <span>{value}</span></>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </Col>
                                  </Row>
                                )}

                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Container>
    </Container>
  );
}
